package com.zi.support.web

import com.zi.support.shared.AppInstalls
import com.zi.support.shared.DeviceHistoryEntry
import com.zi.support.shared.DeviceRegistry
import com.zi.support.shared.SupportDevice
import kotlinx.browser.window
import org.w3c.dom.HTMLElement

/**
 * More > Devices > By phone: each company's phones, with every app's record on them (id, who is logged in, version,
 * last opened). Support adds a phone's IMEI to join records across apps and across factory resets.
 */
internal object DeviceManagementScreen {
    private val icons = mapOf(
        AppInstalls.SUPPORT_APP to "support_agent", AppInstalls.DRIVER_HUB to "local_shipping", AppInstalls.OPERATIONS_APP to "inventory_2",
        AppInstalls.HEATING_APP to "local_fire_department", AppInstalls.ATTENDANCE_APP to "badge", AppInstalls.CARGOVIEW to "visibility",
    )

    fun show() {
        val tabs = DeviceRegistry.companies.joinToString("") { "<button role=\"tab\" data-tab=\"${escape(it)}\">${escape(it)}</button>" }
        element("content")?.innerHTML = """
          $MORE_BACK
          <div class="page-intro"><div><h2>Devices</h2>
            <p>Every company phone and each app on it. Add a phone's IMEI to keep its records together after a reinstall or factory reset.</p></div></div>
          ${viewSwitch(byPhone = true)}
          <div id="dm-company" class="segmented apps-tabs" role="tablist" aria-label="Company">$tabs</div>
          <div class="search-shell dl-search"><span class="material-symbols-rounded" aria-hidden="true">search</span><label class="visually-hidden" for="dm-search">Search phones</label><input id="dm-search" class="input search" placeholder="Search IMEI, phone, driver, model or device number"></div>
          <p id="dm-summary" class="devices-summary">Loading…</p>
          <div id="dm-list" class="dm-list"></div>
        """.trimIndent()
        bindMoreBack()
        bindViewSwitch()
        var company = DeviceRegistry.companies.first()
        var query = ""
        /** Source ("Driver Hub", "supportDevices"…) → its records, so each listener replaces only its own. */
        val bySource = mutableMapOf<String, List<DeviceRegistry.AppRecord>>()
        var registry = emptyList<DeviceRegistry.Registered>()
        /** Record key → its device as More > Devices > By app has it, for the 3D details popup. */
        val raw = mutableMapOf<String, Pair<String, SupportDevice>>()
        var phones = emptyList<DeviceRegistry.Phone>()
        val stops = mutableListOf<dynamic>()
        var timer = 0

        fun render() {
            // No leave hook: once the page is replaced, stop listening.
            val list = element("dm-list") ?: run { stops.forEach { it() }; window.clearInterval(timer); return }
            val buttons = element("dm-company")!!.querySelectorAll("button")
            for (i in 0 until buttons.length) (buttons.item(i) as HTMLElement).let {
                val on = it.getAttribute("data-tab") == company
                it.classList.toggle("active", on); it.setAttribute("aria-selected", on.toString())
            }
            phones = DeviceRegistry.phones(bySource.values.flatten(), registry)
            val shown = DeviceRegistry.shown(phones, company, query)
            val now = (js("Date.now()") as Double).toLong()
            val linked = shown.count { it.imei.isNotEmpty() }
            val support = shown.flatMap { it.records }.mapNotNull { DeviceRegistry.presence(it, now) }
            element("dm-summary")?.textContent = "${shown.size} phone${if (shown.size == 1) "" else "s"} · $linked with an IMEI" +
                (if (support.isEmpty()) "" else " · ${support.count { it == "Online" }} of ${support.size} support phones online") + " · most recently opened first"
            list.innerHTML = if (shown.isEmpty()) "<p class=\"dl-empty\">${if (query.isBlank()) "No phones for $company yet." else "No phones match “${escape(query)}”."}</p>"
            else shown.withIndex().joinToString("") { (index, phone) ->
                val imei = if (phone.imei.isEmpty()) "<span class=\"dm-imei is-missing\">No IMEI</span>"
                    else "<span class=\"dm-imei\"><span class=\"material-symbols-rounded\" aria-hidden=\"true\">verified</span>IMEI ${escape(phone.imei)}</span>"
                val rows = phone.records.joinToString("") { record ->
                    val fresh = AppInstalls.freshness(record.lastOpenedMillis, now)
                    val opened = DeviceRegistry.openedLabel(record, now)
                    val presence = DeviceRegistry.presence(record, now)?.let { " <span class=\"dm-presence${if (it == "Online") " is-online" else ""}\">$it</span>" }.orEmpty()
                    "<li><span class=\"dm-app\"><span class=\"material-symbols-rounded\" aria-hidden=\"true\">${icons[record.app] ?: "apps"}</span>${escape(record.app)}$presence</span>" +
                        "<span class=\"dm-user\">${escape(record.user)}</span>" +
                        "<code title=\"${escape(record.deviceId)}\">${escape(DeviceRegistry.shortId(record))}</code>" +
                        "<span class=\"dm-version\">${escape(record.version.ifBlank { "—" })}</span>" +
                        "<span class=\"dm-opened\" style=\"--fresh:${fresh.hex}\"><i aria-hidden=\"true\"></i>${escape(opened)}</span></li>"
                }
                "<article class=\"dm-phone\" style=\"--i:$index\"><header>" +
                    "<span class=\"dm-phone-icon material-symbols-rounded\" aria-hidden=\"true\">smartphone</span>" +
                    "<button type=\"button\" class=\"dm-phone-main\" data-view=\"${escape(phone.id)}\"><strong>${escape(phone.name)}<span class=\"material-symbols-rounded\" aria-hidden=\"true\">chevron_right</span></strong>" +
                    "<small>$imei<span>${escape(DeviceRegistry.summary(phone, now))}</span></small></button>" +
                    "<button type=\"button\" class=\"btn\" data-phone=\"${escape(phone.id)}\"><span class=\"material-symbols-rounded\" aria-hidden=\"true\">${if (phone.imei.isEmpty()) "add" else "edit"}</span>${if (phone.imei.isEmpty()) "Add IMEI" else "Edit"}</button>" +
                    "</header><ul class=\"dm-records\">" +
                    "<li class=\"dm-records-head\" aria-hidden=\"true\"><span>App</span><span>Logged in</span><span>Device no.</span><span>Version</span><span>Last opened</span></li>$rows</ul></article>"
            }
            val views = list.querySelectorAll("[data-view]")
            for (i in 0 until views.length) (views.item(i) as HTMLElement).let { button ->
                button.addEventListener("click", { phones.firstOrNull { it.id == button.getAttribute("data-view") }?.let { showPhone(it, raw) } })
            }
            val open = list.querySelectorAll("[data-phone]")
            for (i in 0 until open.length) (open.item(i) as HTMLElement).let { button ->
                button.addEventListener("click", { phones.firstOrNull { it.id == button.getAttribute("data-phone") }?.let(::editImei) })
            }
        }

        fun put(source: String, records: List<DeviceRegistry.AppRecord>) { bySource[source] = records; render() }

        // Support App and Driver Hub: Firestore listeners. NotificationCentre apps: one listAppDevices call each.
        val fs = FirebaseGateway.firestore
        AppInstalls.apps.forEach { app ->
            AppInstalls.notificationCentreKey(app)?.let { key ->
                promise(FirebaseGateway.callable("listAppDevices", json("app" to key)), { result ->
                    val rows: Array<dynamic> = result.data.devices
                    put(app, rows.map { row ->
                        val fields: dynamic = row.fields
                        val keys = js("Object.keys")(fields) as Array<String>
                        val history: Array<dynamic> = row.history ?: emptyArray()
                        val device = AppInstalls.centreDevice(app, row.path as String, keys.associateWith { fields[it] as String },
                            (row.updatedAt as Number).toLong(), (row.lastOpenedAt as Number).toLong(), (row.number as? String).orEmpty(), row.active != false,
                            history.map { DeviceHistoryEntry((it.at as Number).toLong(), it.text as String) })
                        DeviceRegistry.record(app, row.path as String, device).also { raw[it.key] = app to device }
                    })
                }, { message -> element("dm-summary")?.textContent = "$app: $message" })
                return@forEach
            }
            AppInstalls.collectionPaths(app).forEach { path ->
                val collection = FirebaseGateway.collection(path)
                val q = if (AppInstalls.activeOnly(app)) fs.query(collection, fs.where("active", "==", true)) else fs.query(collection)
                stops.add(FirebaseGateway.listenQuery(q, { rows ->
                    put(path, rows.map { row ->
                        val keys = js("Object.keys")(row.data) as Array<String>
                        val fields = keys.associateWith { field(row.data, it) }
                        val device = AppInstalls.device(app, path, { field(row.data, it) }, { timestampMillis(row.data[it]).toLong() }, fields)
                        DeviceRegistry.record(app, "$path/${row.id}", device).also { raw[it.key] = app to device }
                    })
                }, { message -> element("dm-summary")?.textContent = message }))
            }
        }
        stops.add(FirebaseGateway.listenQuery(fs.query(FirebaseGateway.collection("deviceRegistry")), { rows ->
            registry = rows.map { row ->
                val links: Array<dynamic> = row.data.links ?: emptyArray()
                DeviceRegistry.Registered(row.id, field(row.data, "name"), links.map { it as String })
            }
            render()
        }, { message -> element("dm-summary")?.textContent = message }))

        val buttons = element("dm-company")!!.querySelectorAll("button")
        for (i in 0 until buttons.length) (buttons.item(i) as HTMLElement).let { button ->
            button.addEventListener("click", { company = button.getAttribute("data-tab").orEmpty(); render() })
        }
        type("dm-search") { query = input("dm-search"); render() }
        bindSearchClear("dm-search") { query = ""; render() }
        render()
        // Re-render each minute so "Opened 5 min ago" stays current.
        timer = window.setInterval({ render() }, 60_000)
    }

    /** One phone: its details, then a section per app with that app's details and every field of its record. */
    private fun showPhone(phone: DeviceRegistry.Phone, raw: Map<String, Pair<String, SupportDevice>>) {
        val now = (js("Date.now()") as Double).toLong()
        fun facts(list: List<com.zi.support.shared.DeviceDetail>) = "<dl class=\"dm-facts\">" +
            list.joinToString("") { "<div><dt>${escape(it.label)}</dt><dd>${escape(it.value)}</dd></div>" } + "</dl>"
        val apps = phone.records.joinToString("") { record ->
            val fresh = AppInstalls.freshness(record.lastOpenedMillis, now)
            "<section class=\"dm-app-section\"><header><span class=\"dm-app-icon material-symbols-rounded\" aria-hidden=\"true\">${icons[record.app] ?: "apps"}</span>" +
                "<div><strong>${escape(record.app)}</strong><small style=\"--fresh:${fresh.hex}\"><i aria-hidden=\"true\"></i>${escape(DeviceRegistry.openedLabel(record, now))}</small></div></header>" +
                facts(DeviceRegistry.recordFacts(record, now)) +
                (if (raw[record.key] == null) "" else "<button type=\"button\" class=\"btn dm-open-install\" data-install=\"${escape(record.key)}\"><span class=\"material-symbols-rounded\" aria-hidden=\"true\">view_in_ar</span>Phone and history</button>") +
                (if (record.details.isEmpty()) "" else "<details class=\"dm-fields\"><summary>All fields (${record.details.size})</summary><dl>" +
                    record.details.joinToString("") { "<dt>${escape(it.label)}</dt><dd>${escape(it.value)}</dd>" } + "</dl></details>") +
                "</section>"
        }
        modal(phone.name, "<div class=\"dm-phone-page\"><h3>Phone</h3>${facts(DeviceRegistry.phoneFacts(phone, now))}<h3>Apps on this phone</h3>$apps</div>",
            "<button type=\"button\" class=\"btn\" id=\"dm-page-imei\"><span class=\"material-symbols-rounded\" aria-hidden=\"true\">${if (phone.imei.isEmpty()) "add" else "edit"}</span>${if (phone.imei.isEmpty()) "Add IMEI" else "Edit IMEI"}</button>",
            wide = true)
        click("dm-page-imei") { editImei(phone) }
        // The same 3D details popup as By app, with this app's other installs for the version check.
        val installs = kotlinx.browser.document.querySelectorAll("[data-install]")
        for (i in 0 until installs.length) (installs.item(i) as HTMLElement).let { button ->
            button.addEventListener("click", {
                raw[button.getAttribute("data-install")]?.let { (app, device) -> AppsScreen.openDevice(app, device, raw.values.filter { it.first == app }.map { it.second }) }
            })
        }
    }

    /** By phone / By app at the top of More > Devices. */
    fun viewSwitch(byPhone: Boolean) = "<div class=\"segmented apps-tabs dm-views\" role=\"tablist\" aria-label=\"View\">" +
        "<button role=\"tab\" id=\"devices-by-phone\" class=\"${if (byPhone) "active" else ""}\" aria-selected=\"$byPhone\">By phone</button>" +
        "<button role=\"tab\" id=\"devices-by-app\" class=\"${if (byPhone) "" else "active"}\" aria-selected=\"${!byPhone}\">By app</button></div>"

    fun bindViewSwitch() {
        click("devices-by-phone") { show() }
        click("devices-by-app") { AppsScreen.show() }
    }

    /** Add or change a phone's IMEI and name; linking saves every record on the card under that IMEI. */
    private fun editImei(phone: DeviceRegistry.Phone) {
        val records = phone.records.joinToString("") { record ->
            "<li><span><strong>${escape(record.app)}</strong><small>${escape(record.user)} · ID ${escape(record.deviceId)}</small></span>" +
                (if (phone.imei.isEmpty() || phone.records.size == 1) "" else "<button type=\"button\" class=\"btn btn-ghost\" data-unlink=\"${escape(record.key)}\">Unlink</button>") + "</li>"
        }
        modal(if (phone.imei.isEmpty()) "Add IMEI" else "Edit phone", """
            <label class="field"><span>IMEI</span><input id="dm-imei" class="input" inputmode="numeric" autocomplete="off" maxlength="20" value="${escape(phone.imei)}" placeholder="15 digits"></label>
            <small class="field-help">Dial *#06# on the phone, or look under Settings › About phone. Use the same IMEI on a phone's new records after a factory reset to join them to its old ones.</small>
            <label class="field dm-name-field"><span>Phone name (optional)</span><input id="dm-name" class="input" maxlength="80" value="${escape(if (phone.imei.isEmpty()) "" else phone.name)}" placeholder="${escape(phone.name)}"></label>
            <p class="dm-modal-label">Records on this phone</p><ul class="dm-modal-records">$records</ul>
            <p id="dm-error" class="dm-error" role="alert" hidden></p>
        """.trimIndent(), "<button type=\"button\" class=\"btn btn-primary\" id=\"dm-save\">Save</button>")
        val email = FirebaseGateway.currentUser()?.email as? String ?: ""
        click("dm-save") {
            val imei = DeviceRegistry.cleanImei(input("dm-imei"))
            if (!DeviceRegistry.isValidImei(input("dm-imei"))) {
                element("dm-error")?.let { it.textContent = "That isn't a valid IMEI: it needs 15 digits, and the last one is a check digit."; (it as HTMLElement).hidden = false }
                return@click
            }
            val keys = phone.records.map { it.key }
            // A changed IMEI: take the records off the old one first.
            if (phone.imei.isNotEmpty() && phone.imei != imei) {
                FirebaseGateway.update("deviceRegistry/${phone.imei}", json("links" to FirebaseGateway.arrayRemoveAll(keys),
                    "updatedAt" to FirebaseGateway.serverTimestamp(), "updatedBy" to email))
            }
            promise(FirebaseGateway.set("deviceRegistry/$imei", json("imei" to imei, "name" to input("dm-name"), "links" to FirebaseGateway.arrayUnionAll(keys),
                "updatedAt" to FirebaseGateway.serverTimestamp(), "updatedBy" to email), merge = true), {
                closeAllModals(); toast("IMEI saved")
            })
        }
        val unlinks = kotlinx.browser.document.querySelectorAll("[data-unlink]")
        for (i in 0 until unlinks.length) (unlinks.item(i) as HTMLElement).let { button ->
            button.addEventListener("click", {
                promise(FirebaseGateway.update("deviceRegistry/${phone.imei}", json("links" to FirebaseGateway.arrayRemoveAll(listOf(button.getAttribute("data-unlink")!!)),
                    "updatedAt" to FirebaseGateway.serverTimestamp(), "updatedBy" to email)), { closeAllModals(); toast("Record unlinked") })
            })
        }
    }
}
